/* eslint-disable no-console */
import React, { useState, useEffect, useRef } from 'react';
import {
  useParams,
  useSearchParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { loginWithSSONEWApi, getAppConstants } from '../../api/loginApi';
import {
  setStringInLs,
  setDataInLS,
} from '../../shared/helper-utils/localStorage';
import { NAVIGATION_ROUTES } from '../../shared/constants/Constants';
import AppLoading from '../../shared/components/app-loading/AppLoading';
import AppAlert from '../../shared/components/app-alert/AppAlert';
import {
  APP_CONFIG,
  APP_USER_AUTH,
  APP_USER_PROFILE,
  APP_EMPLOYEE_EMAIL,
} from '../../saas/AppStorageStaticNames';

const safeDecode = (str) => {
  if (!str) return '';
  try {
    return decodeURIComponent(str);
  } catch (e) {
    return str;
  }
};

// Splits path segments into userid/password/sessiontoken/email/name.
// New format ends with /:email/:name (detected by '@' in the second-last segment);
// password may itself contain '/', so it takes whatever is left in the middle.
const parsePathParts = (parts) => {
  const hasEmail =
    parts.length >= 5 && safeDecode(parts[parts.length - 2]).includes('@');
  const tail = hasEmail ? parts.slice(0, parts.length - 2) : parts;
  return {
    u: tail[0],
    p: tail.length >= 3 ? tail.slice(1, tail.length - 1).join('/') : tail[1],
    s: tail.length >= 3 ? tail[tail.length - 1] : undefined,
    e: hasEmail ? parts[parts.length - 2] : undefined,
  };
};

function SSOAuthTS() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState(undefined);
  const hasExecutedRef = useRef(false);

  const extractParams = () => {
    let u =
      params.userid ||
      searchParams.get('userid') ||
      searchParams.get('agent_userid') ||
      '';
    let p =
      params.password ||
      searchParams.get('password') ||
      searchParams.get('agent_password') ||
      '';
    let s =
      params.sessiontoken ||
      searchParams.get('sessiontoken') ||
      searchParams.get('token') ||
      '';
    let e = params.email || searchParams.get('email') || '';

    // If wildcard path was used, e.g. /sso-authTS/QAStaff/Z2JG8VUkwOdTPsTRhLoR0g==/test-usertoken-001/a@b.com/Arun
    if ((!u || !p || !s) && params['*']) {
      const parsed = parsePathParts(params['*'].split('/').filter(Boolean));
      u = u || parsed.u || '';
      p = p || parsed.p || '';
      s = s || parsed.s || '';
      e = e || parsed.e || '';
    }

    // Direct pathname fallback
    if ((!u || !p || !s) && location.pathname.includes('sso-authTS')) {
      const pathAfterSSO = location.pathname.split(/sso-authTS\/?/i)[1];
      if (pathAfterSSO) {
        const parts = pathAfterSSO.split('/').filter(Boolean);
        if (parts.length >= 3) {
          const parsed = parsePathParts(parts);
          u = u || parsed.u || '';
          p = p || parsed.p || '';
          s = s || parsed.s || '';
          e = e || parsed.e || '';
        }
      }
    }

    return {
      userid: safeDecode(u),
      password: safeDecode(p),
      sessiontoken: safeDecode(s),
      email: safeDecode(e),
    };
  };

  const handleSSOLogin = async () => {
    const creds = extractParams();

    if (!creds.userid || !creds.password || !creds.sessiontoken) {
      setIsLoading(false);
      setAlert({
        variant: 'error',
        title: 'SSO Login Failed',
        description:
          'Missing required parameters (userid, password, or sessiontoken).',
      });
      return;
    }

    try {
      setIsLoading(true);
      const loginResponse = await loginWithSSONEWApi(creds);

      if (
        !loginResponse ||
        (loginResponse?.ApiStatus?.StatusCode !== 200 &&
          loginResponse?.ApiStatus?.StatusCode !== '200' &&
          loginResponse?.ApiStatus?.Status !== 'OK')
      ) {
        setIsLoading(false);
        setAlert({
          variant: 'error',
          title: 'SSO Login Failed',
          description:
            loginResponse?.ApiStatus?.Message ||
            'Unable to authenticate with SSO server.',
        });
        return;
      }

      // Process Result: "GWT76056@@@3@@@Mr@@@Arun@@@Tripgain@@@2"
      const { Result, TransactionStatus } = loginResponse.ApiStatus;
      if (Result && typeof Result === 'string' && Result.includes('@@@')) {
        const profileInfo = Result.split('@@@');
        const profile = {
          agentId: profileInfo[0],
          profileId: profileInfo[1],
          title: profileInfo[2],
          firstName: profileInfo[3],
          lastName: profileInfo[4],
          systemRoleId: profileInfo[5],
          companyName: profileInfo[6] || '',
        };
        setDataInLS(APP_USER_PROFILE, profile);
      } else if (Result && typeof Result === 'object') {
        setDataInLS(APP_USER_PROFILE, Result);
      }

      // Store auth session token
      const authToken = TransactionStatus || creds.sessiontoken;
      setStringInLs(APP_USER_AUTH, authToken);

      // Employee email is sent as `employeeemail` in the booking call
      setStringInLs(APP_EMPLOYEE_EMAIL, creds.email);

      // Fetch App Constants
      try {
        const response = await getAppConstants();
        if (response?.Root?.Status === 'OK') {
          const { ConfigSetting } = response.Root.Result.ConfigSettings;
          const appConfig = Array.isArray(ConfigSetting)
            ? ConfigSetting
            : ConfigSetting;
          setDataInLS(APP_CONFIG, appConfig);
        }
      } catch (err) {
        console.warn('Failed to load app constants during SSO:', err);
      }

      // Direct login redirect to flight landing page
      window.location.href = NAVIGATION_ROUTES.FLIGHT.LANDING;
    } catch (error) {
      console.error('SSO Login exception:', error);
      setIsLoading(false);
      setAlert({
        variant: 'error',
        title: 'SSO Login Error',
        description:
          error?.message || 'An unexpected error occurred during SSO.',
      });
    }
  };

  useEffect(() => {
    if (!hasExecutedRef.current) {
      hasExecutedRef.current = true;
      handleSSOLogin();
    }
  }, []);

  return (
    <>
      <AppLoading isLoading={isLoading} />
      {alert && (
        <div className="container mt-5">
          <AppAlert
            showIcon
            variant={alert.variant}
            title={alert.title}
            description={alert.description}
            onHide={() => navigate('/login')}
          />
          <div className="text-center mt-3">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/login')}
            >
              Go to Login Page
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default SSOAuthTS;
